import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { FaTimes, FaCheckCircle, FaExclamationCircle, FaBan, FaSearch, FaCheckDouble } from 'react-icons/fa';
import MonthPicker from '../shared/MonthPicker';
import StudentHeader from './StudentHeader';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';
import BulkVerifyModal from './BulkVerifyModal';
import Toast from './Toast';
import useToast from './useToast';

const STATUS_LABEL = {
  pending: 'Pending',
  approved: 'Approved',
  needs_revision: 'Needs Revision',
  rejected: 'Rejected'
};

const currentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Scoping to the teacher's assigned UG subjects happens server-side
// (server/routes/ugCompetencyLogRoutes.js GET /pending-review) — this
// component just renders whatever the backend already restricted.
const ELogbookReview = ({ assignedSubjects, ugBatches }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewLog, setReviewLog] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const { toast, showSuccess, showError } = useToast();

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ status: statusFilter });
      if (subjectFilter) params.set('subjectId', subjectFilter);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/competency-logs/pending-review?${params}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) setLogs(data.logs || []);
    } catch (err) {
      console.error('Error fetching UG competency log queue:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, statusFilter, subjectFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const openReview = (log) => {
    setReviewLog(log);
    setReviewStatus('');
    setFeedback('');
  };

  const submitReview = async () => {
    if (!reviewStatus) {
      alert('Please choose Approve, Needs Revision, or Reject.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/competency-logs/${reviewLog._id}/review`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: reviewStatus, feedback })
      });
      const data = await res.json();
      if (data.success) {
        setReviewLog(null);
        await fetchLogs();
        showSuccess(
          reviewStatus === 'approved' ? 'Entry approved successfully'
            : reviewStatus === 'needs_revision' ? 'Entry sent back for revision'
            : 'Entry rejected'
        );
      } else {
        showError(data.message || 'Failed to submit review');
      }
    } catch (err) {
      console.error('Error submitting UG review:', err);
      showError('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const searchLower = searchTerm.trim().toLowerCase();
  const currentMonth = currentMonthValue();
  const filteredLogs = logs.filter(log => {
    if (batchFilter) {
      const studentBatchId = log.studentId?.profile?.ugBatchId?._id || log.studentId?.profile?.ugBatchId;
      if (!studentBatchId || studentBatchId.toString() !== batchFilter) return false;
    }

    // Month filter (same rule PG's ViewLogbooks.js uses): current month
    // shows everything from this month plus any still-unreviewed entries
    // from earlier months (so nothing slips through the cracks); a
    // specifically-selected past month shows only that month's entries.
    const d = new Date(log.dateOfEncounter);
    const logMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (selectedMonth === currentMonth) {
      if (logMonth !== currentMonth && log.status !== 'pending') return false;
    } else if (logMonth !== selectedMonth) {
      return false;
    }

    if (!searchLower) return true;
    const name = `${log.studentId?.profile?.firstName || ''} ${log.studentId?.profile?.lastName || ''}`.toLowerCase();
    return name.includes(searchLower);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, subjectFilter, batchFilter, selectedMonth, searchTerm]);

  const pageLogs = filteredLogs.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const bulkCandidates = filteredLogs.filter(l => l.status !== 'approved');

  return (
    <div className="ugtr-root">
      <Toast toast={toast} />
      <div className="ugtr-header">
        <div>
          <div className="ugtr-header-title">E-Logbook Review</div>
          <div className="ugtr-header-sub">{filteredLogs.length} entries</div>
        </div>
        <button
          className="ugtr-bulk-btn"
          onClick={() => setShowBulkModal(true)}
          disabled={bulkCandidates.length === 0}
        >
          <FaCheckDouble /> Bulk Verify
        </button>
      </div>

      <div className="ugtr-filters-row">
        <div className="ugtr-search-box">
          <FaSearch className="ugtr-search-icon" />
          <input
            type="text"
            placeholder="Search by student name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="ugtr-filter-group">
          <div className="ugtr-filter-item">
            <label>Batch</label>
            <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}>
              <option value="">All Batches</option>
              {(ugBatches || []).map(b => (
                <option key={b._id} value={b._id}>{b.batchLabel}</option>
              ))}
            </select>
          </div>

          <div className="ugtr-filter-item">
            <label>Subject</label>
            <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}>
              <option value="">All my subjects</option>
              {(assignedSubjects || []).map(s => (
                <option key={s._id} value={s._id}>{s.name} ({s.code})</option>
              ))}
            </select>
          </div>

          <div className="ugtr-filter-item">
            <label>Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="needs_revision">Needs Revision</option>
              <option value="rejected">Rejected</option>
              <option value="all">All statuses</option>
            </select>
          </div>

          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
        </div>
      </div>

      {loading ? (
        <div className="ugtr-empty">Loading entries...</div>
      ) : filteredLogs.length === 0 ? (
        <div className="ugtr-empty">No entries match these filters.</div>
      ) : (
        <>
          <div className="ugtr-list">
            {pageLogs.map(log => (
              <div key={log._id} className="ugtr-entry-card">
                <StudentHeader
                  student={log.studentId}
                  ugBatches={ugBatches}
                  statusBadge={(
                    <span className={`ugtr-status-badge ugtr-status-${log.status}`}>
                      {STATUS_LABEL[log.status] || log.status}
                    </span>
                  )}
                />

                <div className="ugtr-entry-body">
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Subject</span>
                    <span className="ugtr-info-value">{log.subjectId?.name}</span>
                  </div>
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Competency</span>
                    <span className="ugtr-info-value">{log.competencyCode}</span>
                  </div>
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Level Achieved</span>
                    <span className="ugtr-info-value">{log.levelAchieved}</span>
                  </div>
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Setting</span>
                    <span className="ugtr-info-value">{log.settingType}</span>
                  </div>
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Date of Encounter</span>
                    <span className="ugtr-info-value">{new Date(log.dateOfEncounter).toLocaleDateString()}</span>
                  </div>
                  <div className="ugtr-info-item ugtr-entry-body-full">
                    <span className="ugtr-info-label">Competency Text</span>
                    <span className="ugtr-info-value">{log.competencySnapshot?.text}</span>
                  </div>
                  {log.caseContext && (
                    <div className="ugtr-info-item ugtr-entry-body-full">
                      <span className="ugtr-info-label">Case Context</span>
                      <span className="ugtr-info-value">{log.caseContext}</span>
                    </div>
                  )}
                  {log.note && (
                    <div className="ugtr-info-item ugtr-entry-body-full">
                      <span className="ugtr-info-label">Note</span>
                      <span className="ugtr-info-value">{log.note}</span>
                    </div>
                  )}
                  {log.review?.feedback && (
                    <div className="ugtr-info-item ugtr-entry-body-full">
                      <span className="ugtr-info-label">Previous Feedback ({log.review.reviewerName})</span>
                      <span className="ugtr-info-value">{log.review.feedback}</span>
                    </div>
                  )}
                </div>

                <div className="ugtr-entry-actions">
                  <button className="ugtr-review-btn" onClick={() => openReview(log)}>
                    Review Entry
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination currentPage={currentPage} totalCount={filteredLogs.length} onPageChange={setCurrentPage} />
        </>
      )}

      {reviewLog && (
        <div className="ugtr-modal-overlay" onClick={() => !submitting && setReviewLog(null)}>
          <div className="ugtr-modal" onClick={e => e.stopPropagation()}>
            <div className="ugtr-modal-head">
              <h3>Review entry</h3>
              <FaTimes onClick={() => !submitting && setReviewLog(null)} />
            </div>

            <p className="ugtr-modal-competency">{reviewLog.competencyCode} — {reviewLog.competencySnapshot?.text}</p>

            <div className="ugtr-decision-row">
              <button
                className={`ugtr-decision-btn ugtr-approve ${reviewStatus === 'approved' ? 'ugtr-decision-active' : ''}`}
                onClick={() => setReviewStatus('approved')}
              >
                <FaCheckCircle /> Approve
              </button>
              <button
                className={`ugtr-decision-btn ugtr-revision ${reviewStatus === 'needs_revision' ? 'ugtr-decision-active' : ''}`}
                onClick={() => setReviewStatus('needs_revision')}
              >
                <FaExclamationCircle /> Needs Revision
              </button>
              <button
                className={`ugtr-decision-btn ugtr-reject ${reviewStatus === 'rejected' ? 'ugtr-decision-active' : ''}`}
                onClick={() => setReviewStatus('rejected')}
              >
                <FaBan /> Reject
              </button>
            </div>

            <label className="ugtr-form-label">Feedback (optional)</label>
            <textarea
              className="ugtr-form-textarea"
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="Notes for the student..."
            />

            <div className="ugtr-modal-actions">
              <button className="ugtr-btn-secondary" onClick={() => setReviewLog(null)} disabled={submitting}>Cancel</button>
              <button className="ugtr-btn-primary" onClick={submitReview} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <BulkVerifyModal
          title="Bulk Verify E-Logbook Entries"
          items={bulkCandidates}
          getId={l => l._id}
          renderItemLabel={l => (
            <>
              <strong>{l.studentId?.profile?.firstName} {l.studentId?.profile?.lastName}</strong>
              <span className="ugtr-bulk-item-meta">
                {l.competencyCode} · {new Date(l.dateOfEncounter).toLocaleDateString()}
              </span>
            </>
          )}
          renderForm={(formState, setFormState) => (
            <>
              <div className="ugtr-decision-row">
                <button
                  type="button"
                  className={`ugtr-decision-btn ugtr-approve ${formState.status === 'approved' ? 'ugtr-decision-active' : ''}`}
                  onClick={() => setFormState({ ...formState, status: 'approved' })}
                >
                  <FaCheckCircle /> Approve
                </button>
                <button
                  type="button"
                  className={`ugtr-decision-btn ugtr-revision ${formState.status === 'needs_revision' ? 'ugtr-decision-active' : ''}`}
                  onClick={() => setFormState({ ...formState, status: 'needs_revision' })}
                >
                  <FaExclamationCircle /> Needs Revision
                </button>
                <button
                  type="button"
                  className={`ugtr-decision-btn ugtr-reject ${formState.status === 'rejected' ? 'ugtr-decision-active' : ''}`}
                  onClick={() => setFormState({ ...formState, status: 'rejected' })}
                >
                  <FaBan /> Reject
                </button>
              </div>
              <label className="ugtr-form-label">Feedback (optional)</label>
              <textarea
                className="ugtr-form-textarea"
                value={formState.feedback || ''}
                onChange={e => setFormState({ ...formState, feedback: e.target.value })}
                placeholder="Notes for the students..."
              />
            </>
          )}
          isValid={formState => !!formState.status}
          onConfirm={async (selectedItems, formState) => {
            const results = await Promise.allSettled(
              selectedItems.map(l => fetch(`${collegeConfig.api.baseURL}/ug/competency-logs/${l._id}/review`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status: formState.status, feedback: formState.feedback })
              }))
            );
            const successCount = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
            setShowBulkModal(false);
            await fetchLogs();
            if (successCount === selectedItems.length) {
              showSuccess(`Successfully verified ${successCount} entries`);
            } else {
              showError(`Verified ${successCount} of ${selectedItems.length} entries. Some failed — please retry those.`);
            }
          }}
          onClose={() => setShowBulkModal(false)}
        />
      )}
    </div>
  );
};

export default ELogbookReview;
